import ndarray from 'ndarray';
import loadImage from 'blueimp-load-image';
import ops from 'ndarray-ops';
import {
  SCALE_RATIO,
  EXPECT_FACE_SIDE,
  HEADSHOT_MARGIN,
  MODEL_FILEPATH,
  INPUT_DIMS,
  OUTPUT_DIMS,
  IOU_THRESH,
  CONF_THRESH,
  RESHAPE_DIMS,
} from '@/constants/faceDetection';
import {
  checkFileResolutionValid,
  checkDetectedFaceNumberValid,
  checkAnyFaceDetected,
  checkAspectRatioRangeValid,
} from '@/utils/faceDetectionUtil';
import {
  classNms,
  getValueAtIndices,
  reshapeTensor,
  createInferenceSession,
  getTensorData,
} from '@/utils/tensorProcessUtil';
import getMFEPath from '@/utils/getMFEPath';

class FaceAlignService {
  static instance = null;

  constructor() {
    this.alignImgElement = null;
    this.landmarks = null;
    this.detectBox = null;
    this.scale = null;
    this.startX = 0;
    this.startY = 0;
    this.extendable = null;
    this.faceModel = null;
    this.session = null;
    this.imageWidth = 0;
    this.imageHeight = 0;
  }

  getFaceAlignElement() {
    return this.alignImgElement;
  }

  setFaceAlignElement(value) {
    this.alignImgElement = value;
  }

  getLandmarks() {
    return this.landmarks;
  }

  setLandmarks(value) {
    this.landmarks = value;
  }

  getScale() {
    return this.scale;
  }

  setScale(value) {
    this.scale = value;
  }

  setStartX(value) {
    this.startX = value;
  }

  setStartY(value) {
    this.startY = value;
  }

  getDetectBox() {
    return this.detectBox;
  }

  setDetectBox(x, y, width, height) {
    this.detectBox = {
      x,
      y,
      width,
      height,
    };
  }

  setExtendable(value) {
    this.extendable = value;
  }

  getFaceModel() {
    return this.faceModel;
  }

  setFaceModel(faceModel) {
    this.faceModel = faceModel;
  }

  getModelSession() {
    return this.session;
  }

  setModelSession(session) {
    this.session = session;
  }

  getImageWidth() {
    return this.imageWidth;
  }

  setImageWidth(width) {
    this.imageWidth = width;
  }

  getImageHeight() {
    return this.imageHeight;
  }

  setImageHeight(height) {
    this.imageHeight = height;
  }

  getLoadedImage() {
    return this.loadedImage;
  }

  setLoadedImage(loadedImage) {
    this.loadedImage = loadedImage;
  }

  static createCenteredMarginImageData(loadedImg) {
    const originalWidth = loadedImg?.image.width;
    const originalHeight = loadedImg?.image.height;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = originalWidth * 2;
    canvas.height = originalHeight * 2;

    const offsetX = (canvas.width - originalWidth) / 2;
    const offsetY = (canvas.height - originalHeight) / 2;
    ctx.drawImage(loadedImg?.image, offsetX, offsetY);
    return {
      centerMarginImage: ctx.getImageData(0, 0, canvas.width, canvas.height),
      originalWidth,
      originalHeight,
    };
  }

  static async createModelInputImageData(inputImage, originalWidth, originalHeight, isCenterMargin) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const [, , height, width] = INPUT_DIMS;
    canvas.height = height;
    canvas.width = width;
    const scale = originalWidth / originalHeight;
    let imgWidth, imgHeight;
    if (scale > 1) {
      imgWidth = canvas.width;
      imgHeight = canvas.height / scale;
    } else {
      imgWidth = canvas.width * scale;
      imgHeight = canvas.height;
    }
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const x = isCenterMargin ? centerX - imgWidth / 2 : 0;
    const y = isCenterMargin ? centerY - imgHeight / 2 : 0;
    const destWidth = isCenterMargin ? imgWidth : canvas.width;
    const destHeight = isCenterMargin ? imgHeight : canvas.height;
    const { image } = isCenterMargin ? { image: await createImageBitmap(inputImage) } : await loadImage(inputImage);
    ctx.drawImage(image, 0, 0, image.width, image.height, x, y, destWidth, destHeight);
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  updateImageProperties(image) {
    if (!image) {
      return;
    }

    this.setLoadedImage(image);
    this.setImageHeight(image.height);
    this.setImageWidth(image.width);
  }

  async createFaceAlignElement(sourceImage, refs, autoPadding) {
    const loadedImg = await loadImage(sourceImage);
    if (!autoPadding) {
      const { image, originalWidth, originalHeight } = loadedImg;
      this.updateImageProperties(image);
      const isFaceResolutionValid = checkFileResolutionValid(originalWidth, originalHeight);
      if (!isFaceResolutionValid) {
        FaceAlignService.showInsufficientResolutionDialogue(refs);
        throw new Error('insufficient resolution');
      }
      // eslint-disable-next-line max-len
      const modelInputImage = await FaceAlignService.createModelInputImageData(
        sourceImage,
        originalWidth,
        originalHeight,
        autoPadding,
      );
      this.setFaceAlignElement(modelInputImage);
    } else {
      // eslint-disable-next-line max-len
      const { centerMarginImage, originalWidth, originalHeight } =
        FaceAlignService.createCenteredMarginImageData(loadedImg);
      // eslint-disable-next-line max-len
      const modelInputImage = await FaceAlignService.createModelInputImageData(
        centerMarginImage,
        originalWidth,
        originalHeight,
        autoPadding,
      );
      this.setFaceAlignElement(modelInputImage);

      const bitmap = await createImageBitmap(this.getFaceAlignElement());
      this.updateImageProperties(bitmap);
    }
  }

  enlargeVerticalRangeByCropHeight(checkRange, landmarks, scaleRatio) {
    const cropHeight = landmarks.roi.h * scaleRatio - landmarks.roi.h / 2;

    const calculatedExtendY = landmarks.roi.y - cropHeight;
    const extendY = checkRange ? Math.max(0, calculatedExtendY) : calculatedExtendY;

    const calculatedExtendHeight = landmarks.roi.h * scaleRatio * 2;
    const extendHeight = checkRange ? Math.max(0, calculatedExtendHeight) : calculatedExtendHeight;

    let y, height;
    if (checkRange) {
      const alignImgElementHeight = this.imageHeight;
      y = extendY + extendHeight > alignImgElementHeight ? landmarks.roi.y : extendY;
      height = extendY + extendHeight > alignImgElementHeight ? alignImgElementHeight : extendHeight;
    } else {
      y = extendY;
      height = extendHeight;
    }
    return { y, height };
  }

  enlargeHorizontalRangeByCropWidth(checkRange, landmarks, scaleRatio) {
    const cropWidth = landmarks.roi.w * scaleRatio - landmarks.roi.w / 2;

    const calculatedExtendX = landmarks.roi.x - cropWidth;
    const extendX = checkRange ? Math.max(0, calculatedExtendX) : calculatedExtendX;

    const calculatedExtendWidth = landmarks.roi.w * scaleRatio * 2;
    const extendWidth = checkRange ? Math.max(0, calculatedExtendWidth) : calculatedExtendWidth;
    let x, width;
    if (checkRange) {
      const alignImgElementWidth = this.imageWidth;

      x = extendX + extendWidth > alignImgElementWidth ? landmarks.roi.x : extendX;
      width = extendX + extendWidth > alignImgElementWidth ? alignImgElementWidth : extendWidth;
    } else {
      x = extendX;
      width = extendWidth;
    }
    return { x, width };
  }

  getEnlargedDetectBox() {
    const { roi } = this.landmarks;
    const rate = Math.max(roi.w, roi.h) / Math.min(roi.w, roi.h);
    const isWidthLonger = roi.w > roi.h;
    // eslint-disable-next-line max-len
    const { x, width } = this.enlargeHorizontalRangeByCropWidth(
      true,
      this.landmarks,
      isWidthLonger ? SCALE_RATIO : SCALE_RATIO * rate,
    );
    // eslint-disable-next-line max-len
    const { y, height } = this.enlargeVerticalRangeByCropHeight(
      true,
      this.landmarks,
      isWidthLonger ? SCALE_RATIO * rate : SCALE_RATIO,
    );

    return {
      x,
      y,
      width,
      height,
    };
  }

  computeProperImageScale(extendable, width, height) {
    const minDimension = Math.min(width, height);
    const maxDimension = Math.max(width, height);

    const scale = extendable ? EXPECT_FACE_SIDE / minDimension : (EXPECT_FACE_SIDE - HEADSHOT_MARGIN) / maxDimension;
    this.setScale(scale);
  }

  scaleAndPositionImageToCanvas(imageElement, extendable, detectBox, scale) {
    const newWidth = detectBox.width * scale;
    const newHeight = detectBox.height * scale;
    const middleX = extendable ? newWidth / 2 : EXPECT_FACE_SIDE / 2;
    const middleY = extendable ? newHeight / 2 : EXPECT_FACE_SIDE / 2;
    const startX = middleX - newWidth / 2;
    const startY = middleY - newHeight / 2;

    this.setStartX(startX);
    this.setStartY(startY);

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = extendable ? newWidth : EXPECT_FACE_SIDE;
    tempCanvas.height = extendable ? newHeight : EXPECT_FACE_SIDE;

    const tempContext = tempCanvas.getContext('2d');
    if (extendable) {
      tempContext.drawImage(imageElement, 0, 0, newWidth, newHeight);
    } else {
      tempContext.drawImage(
        imageElement,
        detectBox.x,
        detectBox.y,
        detectBox.width,
        detectBox.height,
        startX,
        startY,
        newWidth,
        newHeight,
      );
    }

    this.setFaceAlignElement(tempCanvas);
  }

  computeExtendable() {
    const { roi } = this.landmarks;
    const rate = Math.max(roi.w, roi.h) / Math.min(roi.w, roi.h);
    const isWidthLonger = roi.w > roi.h;
    // eslint-disable-next-line max-len
    const { x, width } = this.enlargeHorizontalRangeByCropWidth(
      true,
      this.landmarks,
      isWidthLonger ? SCALE_RATIO : SCALE_RATIO * rate,
    );
    // eslint-disable-next-line max-len
    const { y, height } = this.enlargeVerticalRangeByCropHeight(
      true,
      this.landmarks,
      isWidthLonger ? SCALE_RATIO * rate : SCALE_RATIO,
    );

    const result = width !== roi.w && height !== roi.h && x !== roi.x && y !== roi.y;
    this.setExtendable(result);
    return result;
  }

  checkFaceCenterWithMargin(imgElement, extendable) {
    if (!extendable) {
      return false;
    }

    return this.alignExtendableImage(imgElement);
  }

  alignExtendableImage(imgElement) {
    const enlargedDetectBox = this.getEnlargedDetectBox();
    const { x, y, width, height } = enlargedDetectBox;
    this.setDetectBox(x, y, width, height);
    if (!checkAspectRatioRangeValid(height / width)) {
      return false;
    }

    this.computeProperImageScale(true, width, height);
    const tempCanvas = FaceAlignService.drawEnlargedDetectBoxImage(imgElement, enlargedDetectBox);
    const scale = this.getScale();
    this.scaleAndPositionImageToCanvas(tempCanvas, true, enlargedDetectBox, scale);
    return true;
  }

  static drawEnlargedDetectBoxImage(imgElement, enlargedDetectBox) {
    const { x, y, width, height } = enlargedDetectBox;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempContext = tempCanvas.getContext('2d');
    tempContext.drawImage(imgElement, x, y, width, height, 0, 0, width, height);
    return tempCanvas;
  }

  getFaceAlignImageUrl() {
    return this.alignImgElement.toDataURL('image/jpeg');
  }

  static showInsufficientResolutionDialogue(refs) {
    refs.insufficientResolutionDialogue.show();
  }

  static showNoFaceDetectedDialogue(refs) {
    refs.noFaceDetectedDialogue.show();
  }

  static showMultiFacesDetectedDialogue(refs) {
    refs.multiFacesDetectedDialogue.show();
  }

  static showFaceCenterWithMarginDialogue(refs) {
    refs.faceCenterWithMarginDialogue.show();
  }

  async detectAndAlignFace(uploadedProfile, refs) {
    await this.createFaceAlignElement(uploadedProfile, refs, false);
    let detectionWithLandmarks = await this.runFaceDetectionModel(this.getFaceAlignElement());
    const isAnyFaceDetectedFirstCheck = checkAnyFaceDetected(detectionWithLandmarks);

    if (!isAnyFaceDetectedFirstCheck) {
      await this.createFaceAlignElement(uploadedProfile, refs, true);
      detectionWithLandmarks = await this.runFaceDetectionModel(this.getFaceAlignElement());
    }

    const isAnyFaceDetected = checkAnyFaceDetected(detectionWithLandmarks);
    if (!isAnyFaceDetected) {
      FaceAlignService.showNoFaceDetectedDialogue(refs);
      throw new Error('no face detected');
    }
    return detectionWithLandmarks;
  }

  async detectFacesByLib(uploadedProfile, refs) {
    const detectionWithLandmarks = await this.detectAndAlignFace(uploadedProfile, refs);
    const isDetectedFaceNumberValid = checkDetectedFaceNumberValid(detectionWithLandmarks);
    if (!isDetectedFaceNumberValid) {
      FaceAlignService.showMultiFacesDetectedDialogue(refs);
      throw new Error('face number is invalid');
    }
    const [landmarks] = detectionWithLandmarks;
    const {
      roi: { w, h },
    } = landmarks;
    const isFaceResolutionValid = checkFileResolutionValid(w, h);
    if (!isFaceResolutionValid) {
      FaceAlignService.showInsufficientResolutionDialogue(refs);
      throw new Error('insufficient resolution');
    }

    this.setLandmarks(landmarks);
    const extendable = this.computeExtendable();
    const isFaceCenterWithMargin = this.checkFaceCenterWithMargin(this.getLoadedImage(), extendable);
    if (!isFaceCenterWithMargin) {
      FaceAlignService.showFaceCenterWithMarginDialogue(refs);
      throw new Error('face is not in center or margin not enough');
    }
    const faceImageUrl = this.getFaceAlignImageUrl();
    return { faceImageUrl };
  }

  async loadModels() {
    if (this.getFaceModel()) {
      return;
    }
    const response = await fetch(getMFEPath(MODEL_FILEPATH));
    const modelFile = await response.arrayBuffer();

    this.setFaceModel(modelFile);
  }

  async initializeSession() {
    try {
      await this.initSession();
    } catch (e) {
      throw new Error('initSession failed');
    }
  }

  static async getInstance() {
    if (!FaceAlignService.instance) {
      FaceAlignService.instance = new FaceAlignService();
      await FaceAlignService.instance.loadModels();
      await FaceAlignService.instance.initializeSession();
    }
    return FaceAlignService.instance;
  }

  static async warmupModel(model, dims) {
    const size = dims.reduce((a, b) => a * b);
    const warmupTensor = getTensorData('float32', new Float32Array(size), dims);
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < size; ++i) {
      warmupTensor.data[i] = Math.random() * 2.0 - 1.0; // random value [-1.0, 1.0)
    }
    try {
      await model.run({ [model.inputNames[0]]: warmupTensor });
    } catch (e) {
      throw new Error('warmupModel failed.');
    }
  }

  static async runModel(model, preprocessedData) {
    try {
      const feeds = { [model.inputNames[0]]: preprocessedData };
      const outputData = await model.run(feeds);
      const output = outputData[model.outputNames[0]];
      return output;
    } catch (error) {
      throw new Error('runModel failed.');
    }
  }

  async runFaceDetectionModel(imageData) {
    const data = FaceAlignService.preprocess(imageData);
    const outputTensor = await FaceAlignService.runModel(this.getModelSession(), data);
    const { width: imageWidth, height: imageHeight } = imageData;
    const scaleW = this.getImageWidth() / imageWidth;
    const scaleH = this.getImageHeight() / imageHeight;
    // eslint-disable-next-line max-len
    const boxes = await FaceAlignService.faceDetectionPostprocess(outputTensor, scaleW, scaleH);
    return boxes;
  }

  async initSession() {
    let session;
    try {
      session = await createInferenceSession(this.getFaceModel());
    } catch (error) {
      console.error('Failed to create inference session:', error);
      return;
    }

    try {
      this.setModelSession(session);
      await FaceAlignService.warmupModel(this.getModelSession(), INPUT_DIMS);
    } catch (error) {
      console.error('Failed to initialize session:', error);
    }
  }

  static preprocess(imageData) {
    const { data, width, height } = imageData;
    const dataTensor = ndarray(new Float32Array(data), [width, height, 4]);
    const dataProcessedTensor = ndarray(new Float32Array(width * height * 3), [1, 3, width, height]);
    ops.assign(dataProcessedTensor.pick(0, 0, null, null), dataTensor.pick(null, null, 0));
    ops.assign(dataProcessedTensor.pick(0, 1, null, null), dataTensor.pick(null, null, 1));
    ops.assign(dataProcessedTensor.pick(0, 2, null, null), dataTensor.pick(null, null, 2));

    const tensor = getTensorData('float32', new Float32Array(width * height * 3), [1, 3, width, height]);
    const maskArray = ndarray(new Float32Array(width * height * 3), [1, 3, width, height]);
    ops.divs(maskArray, dataProcessedTensor, 255);
    tensor.data.set(maskArray.data);
    return tensor;
  }

  static postprocess(output, scaleW, scaleH) {
    const reshapedPredictions = reshapeTensor(output, RESHAPE_DIMS);
    const [rows, cols] = reshapedPredictions.dims;
    const detection = [];
    const getData = (row, col) => getValueAtIndices(reshapedPredictions, [row, col], [cols]);
    const X_INDEX = 0;
    const Y_INDEX = 1;
    const WIDTH_INDEX = 2;
    const HEIGHT_INDEX = 3;
    const PROBABILITY_INDEX = 4;
    const CONFIDENCE_INDEX = 5;
    const LANDMARK_START_INDEX = 6;
    const LANDMARK_SIZE = 5;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < rows; ++i) {
      const prob = getData(i, PROBABILITY_INDEX) * getData(i, CONFIDENCE_INDEX);
      if (prob > CONF_THRESH) {
        const w = getData(i, WIDTH_INDEX) * scaleW;
        const h = getData(i, HEIGHT_INDEX) * scaleH;
        const x = (getData(i, X_INDEX) - getData(i, WIDTH_INDEX) * IOU_THRESH) * scaleW;
        const y = (getData(i, Y_INDEX) - getData(i, HEIGHT_INDEX) * IOU_THRESH) * scaleH;
        const landmarks = [];
        // eslint-disable-next-line no-plusplus
        for (let k = 0; k < LANDMARK_SIZE; ++k) {
          const ptx = getData(i, LANDMARK_START_INDEX + k * 3) * scaleW;
          const pty = getData(i, LANDMARK_START_INDEX + k * 3 + 1) * scaleH;
          landmarks.push(ptx, pty);
        }
        detection.push({
          score: prob,
          roi: {
            x,
            y,
            w,
            h,
          },
          landmark: landmarks,
        });
      }
    }

    const result = classNms(detection, IOU_THRESH);
    return result;
  }

  static async faceDetectionPostprocess(tensor, scaleW, scaleH) {
    try {
      const originalOutput = getTensorData('float32', tensor.data, OUTPUT_DIMS);
      const boxes = await FaceAlignService.postprocess(originalOutput, scaleW, scaleH);
      return boxes;
    } catch (e) {
      throw new Error('faceDetectionPostprocess failed.');
    }
  }
}

export default FaceAlignService;
